import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CssBaseline from '@mui/material/CssBaseline';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import MuiCard from '@mui/material/Card';
import {styled} from '@mui/material/styles';
import Lottie from "lottie-react";
import anime6 from "../animations/anime6.json";
import MenuItem from "@mui/material/MenuItem";
import {InputLabel} from "@mui/material";
import Select from "@mui/material/Select";
import Modal from "@mui/material/Modal";
import Typography from "@mui/material/Typography";
import "../styles/SignupPage.css";


const Card = styled(MuiCard)(({theme}) => ({
    display: 'flex',
    flexDirection: 'column',
    alignSelf: 'center',
    width: '100%',
    padding: theme.spacing(4),
    gap: theme.spacing(2),
    margin: 'auto',
    borderRadius: '20px',
    background: 'rgba(255, 255, 255, 0.9)',
    backdropFilter: 'blur(10px)',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    [theme.breakpoints.up('sm')]: {
        width: '500px',
    },
    ...theme.applyStyles('dark', {
        background: 'rgba(30, 30, 30, 0.9)',
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
    }),
}));

const SignUpContainer = styled(Stack)(({theme}) => ({
    minHeight: '100vh',
    padding: theme.spacing(2),
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    position: 'relative',
    [theme.breakpoints.up('sm')]: {
        padding: theme.spacing(4),
    },
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'linear-gradient(45deg, rgba(52, 152, 219, 0.1) 0%, rgba(155, 89, 182, 0.1) 100%)',
        zIndex: 0,
    },
}));

const StyledButton = styled(Button)(({theme}) => ({
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    borderRadius: '25px',
    padding: '12px 24px',
    fontSize: '16px',
    fontWeight: '600',
    textTransform: 'none',
    boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
    transition: 'all 0.3s ease',
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 6px 20px rgba(102, 126, 234, 0.4)',
        background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
    },
}));

const StyledTextField = styled(TextField)(({theme}) => ({
    '& .MuiOutlinedInput-root': {
        borderRadius: '12px',
        background: 'rgba(255, 255, 255, 0.8)',
        transition: 'all 0.3s ease',
        '&:hover': {
            background: 'rgba(255, 255, 255, 0.9)',
            transform: 'translateY(-1px)',
        },
        '&.Mui-focused': {
            background: 'rgba(255, 255, 255, 1)',
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 15px rgba(102, 126, 234, 0.2)',
        },
    },
}));

const PageTitle = styled(Typography)(({theme}) => ({
    fontSize: '2.2rem',
    fontWeight: '700',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    textAlign: 'center',
    marginBottom: '8px',
}));

const SubTitle = styled(Typography)(({theme}) => ({
    color: '#7f8c8d',
    textAlign: 'center',
    fontSize: '14px',
    lineHeight: '1.5',
    marginBottom: '16px',
}));

export default function SignUp(props) {
    const [emailError, setEmailError] = React.useState(false);
    const [emailErrorMessage, setEmailErrorMessage] = React.useState('');
    const [passwordError, setPasswordError] = React.useState(false);
    const [passwordErrorMessage, setPasswordErrorMessage] = React.useState('');
    const [nameError, setNameError] = React.useState(false);
    const [nameErrorMessage, setNameErrorMessage] = React.useState('');
    const [kvkkAccepted, setKvkkAccepted] = React.useState(false);
    const [kvkkError, setKvkkError] = React.useState(false);
    const [showKvkkModal, setShowKvkkModal] = React.useState(false);


    const validateInputs = () => {
        const email = document.getElementById('email');
        const password = document.getElementById('password');
        const name = document.getElementById('name');

        let isValid = true;

        if (!email.value || !/\S+@\S+\.\S+/.test(email.value)) {
            setEmailError(true);
            setEmailErrorMessage('Please enter a valid email address.');
            isValid = false;
        } else {
            setEmailError(false);
            setEmailErrorMessage('');
        }

        if (!password.value || password.value.length < 6) {
            setPasswordError(true);
            setPasswordErrorMessage('Password must be at least 6 characters long.');
            isValid = false;
        } else {
            setPasswordError(false);
            setPasswordErrorMessage('');
        }

        if (!name.value || name.value.length < 1) {
            setNameError(true);
            setNameErrorMessage('Name is required.');
            isValid = false;
        } else {
            setNameError(false);
            setNameErrorMessage('');
        }
        if (!kvkkAccepted) {
            setKvkkError(true);
            isValid = false;
        } else {
            setKvkkError(false);
        }


        return isValid;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!validateInputs()) return;

        const data = new FormData(event.currentTarget);
        const payload = {
            firstName: data.get('name'),
            lastName: data.get('lastName'),
            email: data.get('email'),
            phone: `${data.get('countryCode')}${data.get('phone')}`,
            password: data.get('password'),
        };
        console.log(payload);

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/signup`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Kayıt başarısız");
            }

            const result = await response.json();
            console.log("Kayıt başarılı:", result);

            // Başarılı kayıt sonrası yönlendirme
            alert("Signup successful!");
            window.location.href = "/signin";

        } catch (error) {
            console.error("Signup error:", error.message);
            alert("Signup failed: " + error.message);
        }
    };


    return (
        <div className="SigninPage">
            <CssBaseline enableColorScheme/>
            <SignUpContainer direction="column" justifyContent="space-between">
                <Card variant="outlined">

                    <PageTitle>Kayıt Ol</PageTitle>
                    <SubTitle>
                        Hesabını oluştur ve MeduVR ile sanal gerçeklik deneyimini yaşa! <br/>
                    </SubTitle>
                    <Divider/>

                    <Lottie animationData={anime6} loop={true} className="loading-animation"
                            style={{width: '75%', height: '75%', margin: '10px auto'}}/>

                    <Box
                        component="form"
                        onSubmit={handleSubmit}
                        sx={{display: 'flex', flexDirection: 'column', gap: 2}}
                    >
                        <FormControl>
                            <StyledTextField
                                autoComplete="name"
                                name="name"
                                required
                                fullWidth
                                id="name"
                                error={nameError}
                                helperText={nameErrorMessage}
                                color={nameError ? 'error' : 'primary'}
                                label={"İsim"}
                            />
                        </FormControl>

                        <FormControl>
                            <StyledTextField
                                autoComplete="lastName"
                                name="lastName"
                                required
                                fullWidth
                                id="lastName"
                                error={nameError}
                                helperText={nameErrorMessage}
                                color={nameError ? 'error' : 'primary'}
                                label={"Soyisim"}
                            />
                        </FormControl>
                        <FormControl>
                            <Box sx={{display: 'flex', gap: 1}}>
                                <FormControl sx={{width: '30%'}}>
                                    <InputLabel id="countryCode-label">Ülke Kodu</InputLabel>
                                    <Select
                                        labelId="countryCode-label"
                                        id="countryCode"
                                        name="countryCode"
                                        defaultValue="+90"
                                        label="Ülke Kodu"
                                    >
                                        <MenuItem value="+90">🇹🇷 +90</MenuItem>
                                        <MenuItem value="+1">🇺🇸 +1</MenuItem>
                                        <MenuItem value="+44">🇬🇧 +44</MenuItem>
                                        <MenuItem value="+49">🇩🇪 +49</MenuItem>
                                        <MenuItem value="+33">🇫🇷 +33</MenuItem>
                                    </Select>
                                </FormControl>

                                <TextField
                                    name="phone"
                                    required
                                    fullWidth
                                    id="phone"
                                    placeholder="5051234567"
                                    label="Telefon"
                                />
                            </Box>
                        </FormControl>


                        <FormControl>
                            <StyledTextField
                                required
                                fullWidth
                                id="email"
                                placeholder="your@email.com"
                                name="email"
                                autoComplete="email"
                                variant="outlined"
                                error={emailError}
                                helperText={emailErrorMessage}
                                color={passwordError ? 'error' : 'primary'}
                                label={"E-mail"}
                            />
                        </FormControl>
                        <FormControl>
                            <StyledTextField
                                required
                                fullWidth
                                name="password"
                                placeholder="••••••"
                                type="password"
                                id="password"
                                autoComplete="new-password"
                                variant="outlined"
                                error={passwordError}
                                helperText={passwordErrorMessage}
                                color={passwordError ? 'error' : 'primary'}
                                label={"Şifre"}
                            />
                        </FormControl>
                        <FormControlLabel
                            control={<Checkbox value="allowExtraEmails" color="primary"/>}
                            label="Gelişmelerden haberdar olmak istiyorum."
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={kvkkAccepted}
                                    onChange={(e) => setKvkkAccepted(e.target.checked)}
                                    color="primary"
                                    required
                                />
                            }
                            label={
                                <span>
            <span
                style={{color: 'blue', textDecoration: 'underline', cursor: 'pointer'}}
                onClick={() => setShowKvkkModal(true)}
            >
                KVKK metnini okudum
            </span>{" "}
                                    ve kabul ediyorum.
        </span>
                            }
                        />
                        {kvkkError && (
                            <span style={{color: 'red', fontSize: '0.8rem', marginLeft: '14px'}}>
        KVKK metnini kabul etmeniz gerekiyor.
    </span>
                        )}

                        <StyledButton
                            type="submit"
                            fullWidth
                            variant="contained"
                            onClick={validateInputs}
                        >
                            Kayıt ol
                        </StyledButton>
                    </Box>
                    <Divider/>

                    <p align={"center"} style={{color: 'gray'}}>
                        Zaten hesabın var mı? <a href="/signin">Giriş yap</a>
                    </p>
                </Card>

            </SignUpContainer>

            <Modal
                open={showKvkkModal}
                onClose={() => setShowKvkkModal(false)}
                aria-labelledby="kvkk-modal-title"
                aria-describedby="kvkk-modal-description"
            >
                <Box className="kvkk-modal">
                    <Typography id="kvkk-modal-title" variant="h6" component="h2" gutterBottom>
                        📄 GİZLİLİK POLİTİKASI
                    </Typography>
                    <Typography variant="body2" paragraph>
                        <strong>MeduVR – Radyolojide Sanal Gerçeklik Platformu</strong>
                        <br/>
                        Son güncelleme: Nisan 2025
                        <br/>
                        Web sitesi: https://www.meduvr.com
                    </Typography>
                    <Typography variant="body2" paragraph>
                        <strong>1. Genel Bilgilendirme</strong>
                        <br/>
                        MeduVR platformu, kullanıcılarının gizliliğine saygı duyar. Bu gizlilik politikası, web sitemizi
                        ve eğitim platformumuzu kullandığınızda toplanan kişisel verilerin nasıl işlendiğini ve
                        korunduğunu açıklamaktadır.
                    </Typography>
                    <Typography variant="body2" paragraph>
                        <strong>2. Toplanan Bilgiler</strong>
                        <br/>
                        Sitemizi ziyaret ettiğinizde veya kayıt olduğunuzda aşağıdaki bilgileri toplayabiliriz:
                        <ul>
                            <li>Ad ve soyad</li>
                            <li>E-posta adresi</li>
                            <li>Eğitim modülü kullanım verileri</li>
                            <li>Tarayıcı ve cihaz bilgileri (IP adresi, oturum süresi, cihaz türü)</li>
                            <li>Geri bildirim içerikleri veya iletişim formları</li>
                        </ul>
                    </Typography>
                    <Typography variant="body2" paragraph>
                        <strong>3. Bilgilerin Kullanım Amaçları</strong>
                        <br/>
                        Toplanan veriler şu amaçlarla kullanılır:
                        <ul>
                            <li>Eğitim içeriği sunmak ve kullanıcı deneyimini özelleştirmek</li>
                            <li>Kurslara erişimi sağlamak ve ilerleme takibi yapmak</li>
                            <li>Sertifika düzenlemek</li>
                            <li>Teknik destek ve iletişim sağlamak</li>
                            <li>Kullanıcı geri bildirimlerini değerlendirmek</li>
                            <li>İstatistiksel analiz ve platform geliştirme</li>
                        </ul>
                        Bilgileriniz üçüncü şahıslarla ticari amaçlarla paylaşılmaz.
                    </Typography>
                    <Typography variant="body2" paragraph>
                        <strong>4. Çerezler (Cookies)</strong>
                        <br/>
                        Web sitemizde kullanıcı deneyimini geliştirmek amacıyla çerezler kullanılmaktadır. Çerezler;
                        tarayıcı tipinizi, ziyaret ettiğiniz sayfaları ve siteyle olan etkileşiminizi kaydederek size
                        daha iyi bir hizmet sunulmasını sağlar. Çerez kullanımını tarayıcı ayarlarınızdan dilediğiniz
                        zaman devre dışı bırakabilirsiniz.
                    </Typography>
                    <Typography variant="body2" paragraph>
                        <strong>5. Veri Güvenliği</strong>
                        <br/>
                        Verileriniz, ulusal ve uluslararası veri güvenliği standartlarına uygun olarak korunur.
                        Sunucularımız ve yazılım sistemlerimiz, yetkisiz erişim ve veri kaybına karşı korunmaktadır.
                    </Typography>
                    <Typography variant="body2" paragraph>
                        <strong>6. Üçüncü Taraf Bağlantılar</strong>
                        <br/>
                        MeduVR platformu, bazı sayfalarda üçüncü taraf web sitelerine bağlantı verebilir. Bu sitelerin
                        kendi gizlilik politikaları geçerlidir. MeduVR, bu sitelerin içerik veya uygulamalarından
                        sorumlu değildir.
                    </Typography>
                    <Typography variant="body2" paragraph>
                        <strong>7. Kullanıcı Hakları</strong>
                        <br/>
                        KVKK ve GDPR kapsamında kullanıcı olarak aşağıdaki haklara sahipsiniz:
                        <ul>
                            <li>Hangi verilerin toplandığını öğrenme</li>
                            <li>Bilgilerinize erişim ve düzeltme talebi</li>
                            <li>Verilerin silinmesini isteme</li>
                            <li>Şikâyet veya başvuru yolları hakkında bilgi alma</li>
                        </ul>
                        Taleplerinizi <a href="mailto:info@meduvr.com">info@meduvr.com</a> adresine iletebilirsiniz.
                    </Typography>
                    <Typography variant="body2" paragraph>
                        <strong>8. Politika Güncellemeleri</strong>
                        <br/>
                        Gizlilik Politikası, yasal zorunluluklar ve hizmet değişiklikleri doğrultusunda güncellenebilir.
                        Güncellenmiş versiyonlar bu sayfada yayınlandığı tarihten itibaren geçerli olur.
                    </Typography>
                    <Typography variant="body2" paragraph>
                        📌 <strong>İletişim</strong>
                        <br/>
                        RSG Bilgi Teknolojileri Sağlık ve İnovasyon Ltd. Şti.
                        <br/>
                        Marmara Üniversitesi Teknopark, İstanbul
                        <br/>
                        E-posta: <a href="mailto:info@meduvr.com">info@meduvr.com</a>
                    </Typography>
                    <Button onClick={() => setShowKvkkModal(false)} sx={{mt: 2}} variant="contained" fullWidth>
                        Kapat
                    </Button>
                </Box>
            </Modal>

        </div>
    );
}
